import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { CornerDownLeft, Layers, Search } from 'lucide-react';
import type { PermissionKey } from '@bright/shared';
import { useAuth } from '@/lib/auth';
import { useScrollLock } from './Modal';

/**
 * Cmd-K.
 *
 * The fastest route from "someone read me a chassis number over the phone" to the
 * vehicle's profile. Typing anything that looks like an identifier offers a lookup as
 * the first result; everything else is a jump to a section. It only ever offers
 * destinations the user actually holds a permission for.
 */

interface Command {
  id: string;
  label: string;
  hint?: string;
  to: string;
  permission: PermissionKey;
  keywords: string;
}

export const COMMANDS: Command[] = [
  { id: 'dashboard', label: 'Dashboard', to: '/', permission: 'analytics:read', keywords: 'home overview summary' },
  { id: 'stock', label: 'Bright stock', to: '/stock', permission: 'vehicles:read', keywords: 'inventory vehicles held warehouse' },
  { id: 'dealers', label: 'Dealers', to: '/dealers', permission: 'parties:read', keywords: 'network partners' },
  { id: 'sub-dealers', label: 'Sub dealers', to: '/sub-dealers', permission: 'parties:read', keywords: 'network partners mitr' },
  { id: 'outlets', label: 'Company outlets', to: '/outlets', permission: 'parties:read', keywords: 'coo company owned' },
  { id: 'search', label: 'Vehicle search', to: '/search', permission: 'vehicles:read', keywords: 'find chassis vin invoice' },
  { id: 'ageing', label: 'Ageing', to: '/ageing', permission: 'vehicles:read', keywords: 'old days stale buckets' },
  { id: 'analytics', label: 'Analytics', to: '/analytics', permission: 'analytics:read', keywords: 'charts trends' },
  { id: 'sales', label: 'Sales', to: '/sales', permission: 'analytics:read', keywords: 'sold monthly leaderboard revenue' },
  { id: 'asm-sales', label: 'ASM sales performance', to: '/sales-performance', permission: 'analytics:read', keywords: 'asm mitr dealer sold month territory counters' },
  { id: 'financiers', label: 'Financiers', to: '/financiers', permission: 'parties:read', keywords: 'finance loans lenders hypothecation' },
  { id: 'alerts', label: 'Alerts', to: '/alerts', permission: 'alerts:read', keywords: 'issues warnings duplicates' },
  { id: 'reports', label: 'Reports', to: '/reports', permission: 'reports:run', keywords: 'excel csv pdf export' },
  { id: 'sync', label: 'Sync', to: '/sync', permission: 'sync:read', keywords: 'google sheet import runs' },
  { id: 'users', label: 'Users', to: '/admin/users', permission: 'users:read', keywords: 'administration accounts' },
  { id: 'roles', label: 'Roles and permissions', to: '/admin/roles', permission: 'roles:manage', keywords: 'administration access' },
  { id: 'settings', label: 'Settings', to: '/admin/settings', permission: 'settings:manage', keywords: 'administration organisation' },
];

/**
 * Chassis numbers, VINs and invoice numbers are alphanumeric and reasonably long.
 * Spaces are allowed inside (29 Sep 2026): a chassis read off the plate is
 * written in groups — "M56BRA ZC25C 000603" — and was offered nothing at all.
 * The search and the passport both read the number past the spaces.
 */
export function looksLikeIdentifier(query: string): boolean {
  const trimmed = query.trim();
  const letters = trimmed.replace(/[^A-Za-z0-9]/g, '');
  return letters.length >= 4 && /^[A-Za-z0-9][A-Za-z0-9/\s-]*$/.test(trimmed) && /\d/.test(trimmed);
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { has } = useAuth();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useScrollLock(open);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      // The input is the whole point of the dialog, so it takes focus immediately.
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const results = useMemo(() => {
    const allowed = COMMANDS.filter((command) => has(command.permission));
    const needle = query.trim().toLowerCase();
    if (!needle) return allowed;
    return allowed.filter(
      (command) =>
        command.label.toLowerCase().includes(needle) ||
        command.keywords.includes(needle),
    );
  }, [query, has]);

  const identifier = looksLikeIdentifier(query) ? query.trim().replace(/\s+/g, ' ') : null;
  const lookup = identifier !== null && has('vehicles:read');
  // A chassis read out whole can go straight to its cross-ledger passport —
  // offered only to holders of passport:read, the staff-only cross-org grant.
  const passport = identifier !== null && has('passport:read');
  const specialCount = (lookup ? 1 : 0) + (passport ? 1 : 0);
  const passportIndex = lookup ? 1 : 0;
  const total = results.length + specialCount;

  const run = useCallback(
    (index: number) => {
      if (identifier !== null) {
        if (lookup && index === 0) {
          navigate(`/search?q=${encodeURIComponent(identifier)}`);
          onClose();
          return;
        }
        if (passport && index === passportIndex) {
          navigate(`/passport/${encodeURIComponent(identifier)}`);
          onClose();
          return;
        }
      }
      const command = results[index - specialCount];
      if (!command) return;
      navigate(command.to);
      onClose();
    },
    [identifier, lookup, passport, passportIndex, specialCount, results, navigate, onClose],
  );

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive((current) => (total === 0 ? 0 : (current + 1) % total));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive((current) => (total === 0 ? 0 : (current - 1 + total) % total));
      } else if (event.key === 'Enter') {
        event.preventDefault();
        run(active);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, total, active, run, onClose]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  // Keep the highlighted row in view when the arrows walk past the fold.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open || typeof document === 'undefined') return null;

  const rowClass = (index: number) =>
    clsx(
      'flex w-full items-center gap-3 px-4 py-2.5 text-left text-micro',
      index === active ? 'bg-surface-2 text-ink' : 'text-ink-2',
    );

  return createPortal(
    <div className="fixed inset-0 z-100 flex items-start justify-center p-4 pt-[12vh] print-hide">
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-[color-mix(in_oklab,var(--c-ink)_45%,transparent)] backdrop-blur-[2px] animate-fade-in"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="relative z-10 flex w-full max-w-lg flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-float animate-slide-up"
      >
        <div className="flex items-center gap-2.5 border-b border-line px-4">
          <Search className="h-4 w-4 shrink-0 text-faint" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search a chassis number, or jump to a section"
            aria-label="Search or jump to a section"
            className="h-12 flex-1 bg-transparent text-body text-ink placeholder:text-faint focus-visible:outline-none"
          />
          <kbd className="shrink-0 rounded-[4px] border border-line px-1 font-data text-label text-faint">
            Esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-80 overflow-y-auto py-1" role="listbox">
          {total === 0 ? (
            <p className="px-4 py-6 text-center text-micro text-muted">
              Nothing matches that.
            </p>
          ) : null}

          {lookup ? (
            <button
              type="button"
              role="option"
              aria-selected={active === 0}
              data-index={0}
              onMouseEnter={() => setActive(0)}
              onClick={() => run(0)}
              className={rowClass(0)}
            >
              <Search className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
              <span className="min-w-0 flex-1 truncate">
                Look up{' '}
                <span className="font-data font-medium text-ink">{identifier}</span>
              </span>
              <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
            </button>
          ) : null}

          {passport ? (
            <button
              type="button"
              role="option"
              aria-selected={active === passportIndex}
              data-index={passportIndex}
              onMouseEnter={() => setActive(passportIndex)}
              onClick={() => run(passportIndex)}
              className={rowClass(passportIndex)}
            >
              <Layers className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
              <span className="min-w-0 flex-1 truncate">
                Open the passport for{' '}
                <span className="font-data font-medium text-ink">{identifier}</span>
              </span>
              <span className="font-data text-label text-faint">both ledgers</span>
            </button>
          ) : null}

          {results.map((command, index) => {
            const rowIndex = index + specialCount;
            return (
              <button
                key={command.id}
                type="button"
                role="option"
                aria-selected={rowIndex === active}
                data-index={rowIndex}
                onMouseEnter={() => setActive(rowIndex)}
                onClick={() => run(rowIndex)}
                className={rowClass(rowIndex)}
              >
                <span className="min-w-0 flex-1 truncate">{command.label}</span>
                <span className="font-data text-label text-faint">{command.to}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
